import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AppNotification } from '../api';
import { IconBell } from './icons';

/**
 * The in-app half of notifications. Email is the half that reaches people who
 * are not looking at the app; this is for the ones who are, and it is also the
 * record of what was sent when mail is down.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const feed = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.notifications(),
    refetchInterval: 60_000,
  });

  const markRead = useMutation({
    mutationFn: (ids: string[] | 'all') => api.markNotificationsRead(ids),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Click-away and Escape, because a panel that will not close is worse than
  // no panel.
  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = feed.data?.unread ?? 0;
  const rows = feed.data?.notifications ?? [];

  return (
    <div ref={wrapper} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
      >
        <IconBell className="h-5 w-5" />
        {unread > 0 && (
          <span
            key={unread}
            className="animate-pop absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-copper-700 px-1 text-[10px] font-semibold tabular-nums text-white ring-2 ring-white dark:ring-slate-950"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg sm:w-96 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <span className="text-sm font-medium text-slate-900 dark:text-white">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markRead.mutate('all')}
                disabled={markRead.isPending}
                className="text-xs font-medium text-brand-700 underline-offset-4 hover:underline disabled:opacity-50 dark:text-brand-300"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {rows.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                Nothing yet. You will hear about approvals, changes and completed work here.
              </p>
            ) : (
              <ul>
                {rows.map((row) => (
                  <Item
                    key={row.id}
                    row={row}
                    onOpen={() => {
                      if (!row.read) markRead.mutate([row.id]);
                      setOpen(false);
                    }}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Item({ row, onOpen }: { row: AppNotification; onOpen: () => void }) {
  const content = (
    <div className={`px-4 py-3 ${row.read ? '' : 'bg-brand-50/60 dark:bg-brand-950/30'}`}>
      <div className="flex items-start gap-2">
        {!row.read && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-600 dark:bg-brand-400" />}
        <div className={`min-w-0 flex-1 ${row.read ? 'pl-3.5' : ''}`}>
          <p className="text-sm font-medium text-slate-900 dark:text-white">{row.title}</p>
          <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-slate-600 dark:text-slate-400">{row.body}</p>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{relative(row.createdAt)}</p>
        </div>
      </div>
    </div>
  );

  return (
    <li className="border-b border-slate-100 last:border-b-0 dark:border-slate-800/70">
      {row.request ? (
        <Link
          to={`/requests/${row.request.id}`}
          onClick={onOpen}
          className="block transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/60"
        >
          {content}
        </Link>
      ) : (
        <button type="button" onClick={onOpen} className="block w-full text-left">
          {content}
        </button>
      )}
    </li>
  );
}

function relative(iso: string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
}
